import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from '../../hooks/use-toast';
import { ShieldPlus, Upload, FileText, Trash2, Eye, CreditCard, Stethoscope, Users } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const formatDate = (d) => {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

const isImageFile = (name) => /\.(jpe?g|png|webp|gif|bmp)$/i.test(name || '');
const isPdfFile = (name) => /\.pdf$/i.test(name || '');

// Auto-fitting preview that matches the uploaded card's real aspect ratio
// (detected on the backend). Images size naturally; PDFs use the ratio box.
const DocPreview = ({ doc, testid }) => {
  const src = `${API}${doc.url}`;
  if (isImageFile(doc.filename)) {
    return (
      <a href={src} target="_blank" rel="noreferrer" className="block mb-2">
        <img src={src} alt={doc.filename} className="w-full h-auto rounded-md border bg-white" data-testid={`ins-${testid}-preview`} />
      </a>
    );
  }
  if (isPdfFile(doc.filename)) {
    const ratio = doc.aspect_ratio || 1.6;
    return (
      <div
        className="w-full rounded-md border bg-gray-50 overflow-hidden mb-2"
        style={{ aspectRatio: String(ratio), maxHeight: '60vh' }}
        data-testid={`ins-${testid}-preview`}
      >
        <object data={`${src}#toolbar=0&navpanes=0&view=Fit`} type="application/pdf" className="w-full h-full block">
          <iframe src={`${src}#toolbar=0&navpanes=0&view=Fit`} title="card-preview" className="w-full h-full" />
          <p className="text-xs text-gray-500 p-3">
            Preview not supported here. <a href={src} target="_blank" rel="noreferrer" className="text-sky-600 underline">Open</a>.
          </p>
        </object>
      </div>
    );
  }
  return null;
};

const InsuranceUploadSection = ({ employee, onUpdated }) => {
  const [docs, setDocs] = useState(employee?.insurance_documents || {});
  const [dependents, setDependents] = useState([]);
  const [busy, setBusy] = useState(null);
  const cardRef = useRef(null);
  const coverageRef = useRef(null);

  useEffect(() => {
    setDocs(employee?.insurance_documents || {});
    // Always fetch authoritative docs (incl. aspect_ratio) from the server,
    // since the employee object passed by list/card views may omit them.
    if (employee?.id) {
      axios.get(`${API}/api/insurance/employee/${employee.id}/info`)
        .then(r => { setDocs(r.data?.insurance_documents || {}); setDependents(r.data?.dependents || []); })
        .catch(() => {});
    }
  }, [employee]);

  const reload = async () => {
    try {
      const r = await axios.get(`${API}/api/insurance/employee/${employee.id}/info`);
      setDocs(r.data?.insurance_documents || {});
      setDependents(r.data?.dependents || []);
      onUpdated?.();
    } catch { /* ignore */ }
  };

  const upload = async (kind, file) => {
    if (!file) return;
    setBusy(kind);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/api/insurance/employee/${employee.id}/${kind}`, fd);
      toast({ title: kind === 'card' ? 'Insurance card uploaded' : 'Coverage document uploaded' });
      await reload();
    } catch (e) {
      toast({ title: 'Upload failed', description: e?.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setBusy(null); }
  };

  const remove = async (kind) => {
    if (!window.confirm('Remove this document?')) return;
    setBusy(kind);
    try {
      await axios.delete(`${API}/api/insurance/employee/${employee.id}/${kind}`);
      await reload();
    } catch { /* ignore */ } finally { setBusy(null); }
  };

  const uploadDep = async (dependentId, file) => {
    if (!file) return;
    setBusy(`dep-${dependentId}`);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/api/insurance/employee/${employee.id}/dependent/${dependentId}/card`, fd);
      toast({ title: "Dependent's insurance card uploaded" });
      await reload();
    } catch (e) {
      toast({ title: 'Upload failed', description: e?.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setBusy(null); }
  };

  const removeDep = async (dependentId) => {
    if (!window.confirm("Remove this dependent's card?")) return;
    setBusy(`dep-${dependentId}`);
    try {
      await axios.delete(`${API}/api/insurance/employee/${employee.id}/dependent/${dependentId}/card`);
      await reload();
    } catch { /* ignore */ } finally { setBusy(null); }
  };

  return (
    <Card data-testid="insurance-upload-section">
      <CardContent className="p-4">
        <h3 className="text-sm font-semibold flex items-center gap-2 mb-3">
          <ShieldPlus className="h-4 w-4 text-emerald-600" /> Health Insurance
        </h3>

        <DocSlot
          label="Insurance Card"
          icon={CreditCard}
          color="sky"
          doc={docs.card}
          busy={busy === 'card'}
          inputRef={cardRef}
          onPick={() => cardRef.current?.click()}
          onFile={(f) => upload('card', f)}
          onRemove={() => remove('card')}
          accept=".pdf,.jpg,.jpeg,.png"
          testid="card"
          preview
        />

        <DocSlot
          label="Hospital Coverage Document"
          icon={Stethoscope}
          color="violet"
          doc={docs.coverage}
          busy={busy === 'coverage'}
          inputRef={coverageRef}
          onPick={() => coverageRef.current?.click()}
          onFile={(f) => upload('coverage', f)}
          onRemove={() => remove('coverage')}
          accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls"
          testid="coverage"
        />

        {dependents.length > 0 && (
          <div className="mt-4" data-testid="dependents-insurance-block">
            <h4 className="text-xs font-semibold text-gray-600 flex items-center gap-2 mb-2">
              <Users className="h-3.5 w-3.5 text-emerald-600" /> Dependents' Insurance Cards
            </h4>
            {dependents.map((dep) => (
              <DependentCardSlot
                key={dep.id}
                dep={dep}
                busy={busy === `dep-${dep.id}`}
                onFile={(f) => uploadDep(dep.id, f)}
                onRemove={() => removeDep(dep.id)}
              />
            ))}
          </div>
        )}

        <p className="text-[10px] text-gray-400 mt-3">
          All documents (employee + dependents) are visible in the employee's portal and can be downloaded by them.
        </p>
      </CardContent>
    </Card>
  );
};

const DependentCardSlot = ({ dep, busy, onFile, onRemove }) => {
  const inputRef = useRef(null);
  return (
    <DocSlot
      label={`${dep.name}${dep.relation ? ` · ${dep.relation}` : ''}`}
      icon={CreditCard}
      color="sky"
      doc={(dep.insurance_documents || {}).card}
      busy={busy}
      inputRef={inputRef}
      onPick={() => inputRef.current?.click()}
      onFile={onFile}
      onRemove={onRemove}
      accept=".pdf,.jpg,.jpeg,.png"
      testid={`dep-${dep.id}`}
      preview
    />
  );
};

const DocSlot = ({ label, icon: Icon, color, doc, busy, inputRef, onPick, onFile, onRemove, accept, testid, preview }) => {
  const has = !!doc?.url;
  const colorMap = {
    sky: 'bg-sky-50 text-sky-700 border-sky-200',
    violet: 'bg-violet-50 text-violet-700 border-violet-200',
  };
  return (
    <div className={`rounded-lg border p-3 mb-3 ${colorMap[color]}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="h-4 w-4" />
        <span className="text-sm font-semibold flex-1">{label}</span>
        {has && (
          <span className="text-[10px] text-gray-500">Updated {formatDate(doc.uploaded_at)}</span>
        )}
      </div>
      {has && preview && <DocPreview doc={doc} testid={testid} />}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => onFile(e.target.files?.[0])}
        data-testid={`ins-${testid}-input`}
      />
      <div className="flex flex-wrap gap-2">
        {has ? (
          <>
            <a
              href={`${API}${doc.url}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs bg-white border hover:bg-gray-50"
              data-testid={`ins-${testid}-view`}
            >
              <Eye className="h-3 w-3" /> View
              <span className="ml-1 text-gray-400 truncate max-w-[120px]">{doc.filename}</span>
            </a>
            <Button type="button" size="sm" variant="outline" onClick={onPick} disabled={busy} data-testid={`ins-${testid}-replace`}>
              <Upload className="h-3.5 w-3.5 mr-1" /> Replace
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={onRemove} disabled={busy} className="text-rose-600" data-testid={`ins-${testid}-delete`}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </>
        ) : (
          <Button type="button" size="sm" onClick={onPick} disabled={busy} data-testid={`ins-${testid}-upload`}>
            <Upload className="h-3.5 w-3.5 mr-1" /> {busy ? 'Uploading…' : 'Upload'}
          </Button>
        )}
      </div>
    </div>
  );
};

export default InsuranceUploadSection;
